import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useSnackbar } from 'notistack5';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Avatar,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  Tooltip,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Autocomplete,
  InputAdornment
} from '@material-ui/core';
import { Icon } from '@iconify/react';
import videoFill from '@iconify/icons-eva/video-fill';
import fileFill from '@iconify/icons-eva/file-fill';
import plusFill from '@iconify/icons-eva/plus-fill';
import editFill from '@iconify/icons-eva/edit-fill';
import trash2Fill from '@iconify/icons-eva/trash-2-fill';
import linkFill from '@iconify/icons-eva/link-fill';
import downloadFill from '@iconify/icons-eva/download-fill';
import playCircleFill from '@iconify/icons-eva/play-circle-fill';
import eyeFill from '@iconify/icons-eva/eye-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import shareFill from '@iconify/icons-eva/share-fill';
import personFill from '@iconify/icons-eva/person-fill';

import Page from '../components/Page';
import usersAPI from '../utils/api/users';
import storage from '../utils/storage';
import { logSuccessAction, ACTION_CATEGORIES } from '../utils/actionLogger';

// Portal component to render content outside the root div
function Portal({ children }) {
  const portalRoot = document.getElementById('portal-root');
  if (!portalRoot) {
    console.warn('Portal root element not found');
    return children; // Fallback to regular rendering
  }
  return ReactDOM.createPortal(children, portalRoot);
}

// Tab Panel Component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`training-tabpanel-${index}`}
      aria-labelledby={`training-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Training Video Card Component
function TrainingVideoCard({ video, onEdit, onDelete, onView }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getVideoThumbnail = (url) => {
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
      return videoId ? `https://img.youtube.com/vi/${videoId[1]}/mqdefault.jpg` : null;
    }
    return null;
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ position: 'relative' }}>
        {video.urls && video.urls.length > 0 && getVideoThumbnail(video.urls[0].url) ? (
          <Box
            component="img"
            src={getVideoThumbnail(video.urls[0].url)}
            alt={video.title}
            sx={{
              width: '100%',
              height: 200,
              objectFit: 'cover'
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: 200,
              backgroundColor: 'grey.200',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Icon icon={videoFill} width={60} height={60} color={theme.palette.grey[400]} />
          </Box>
        )}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: 1,
            px: 1,
            py: 0.5
          }}
        >
          <Typography variant="caption" sx={{ color: 'white' }}>
            {video.urls ? video.urls.length : 0} Videos
          </Typography>
        </Box>
      </Box>
      
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" gutterBottom noWrap>
          {video.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {video.description}
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Chip
            label={video.category || 'General'}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ mr: 1, mb: 1 }}
          />
          <Chip
            label={video.difficulty || 'Beginner'}
            size="small"
            color="secondary"
            variant="outlined"
            sx={{ mr: 1, mb: 1 }}
          />
          {video.isPublic && (
            <Chip
              label="Public"
              size="small"
              color="success"
              variant="outlined"
              sx={{ mr: 1, mb: 1 }}
            />
          )}
        </Box>
        
        {video.tags && video.tags.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {video.tags.slice(0, 3).map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                variant="filled"
                sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }}
              />
            ))}
            {video.tags.length > 3 && (
              <Chip
                label={`+${video.tags.length - 3}`}
                size="small"
                variant="filled"
                sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        )}
        
        {video.urls && video.urls.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {video.urls.slice(0, 2).map((urlItem, index) => (
              <Chip
                key={index}
                label={urlItem.title || `Video ${index + 1}`}
                size="small"
                variant="outlined"
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
            {video.urls.length > 2 && (
              <Chip
                label={`+${video.urls.length - 2} more`}
                size="small"
                variant="outlined"
                sx={{ mr: 1, mb: 1 }}
              />
            )}
          </Box>
        )}
        
        <Typography variant="caption" color="text.secondary">
          Created: {new Date(video.createdAt).toLocaleDateString()}
        </Typography>
      </CardContent>

      <CardActions>
        <Button
          size="small"
          startIcon={<Icon icon={eyeFill} />}
          onClick={() => onView(video)}
        >
          View
        </Button>
        <Button
          size="small"
          startIcon={<Icon icon={editFill} />}
          onClick={() => onEdit(video)}
        >
          Edit
        </Button>
        <Button
          size="small"
          color="error"
          startIcon={<Icon icon={trash2Fill} />}
          onClick={() => onDelete(video.id)}
        >
          Delete
        </Button>
      </CardActions>
    </Card>
  );
}

// Training Module Card Component
function TrainingModuleCard({ module, onEdit, onDelete, onView }) {
  const theme = useTheme();

  const getFileIcon = (fileType) => {
    if (fileType.includes('pdf')) return fileFill;
    if (fileType.includes('doc')) return fileFill;
    if (fileType.includes('image')) return fileFill;
    return fileFill;
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          height: 200,
          backgroundColor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}
      >
        <Icon icon={getFileIcon(module.fileType || 'pdf')} width={80} height={80} color={theme.palette.primary.main} />
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: 1,
            px: 1,
            py: 0.5
          }}
        >
          <Typography variant="caption" sx={{ color: 'white' }}>
            {module.files ? module.files.length : 0} Files
          </Typography>
        </Box>
      </Box>
      
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" gutterBottom noWrap>
          {module.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {module.description}
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Chip
            label={module.category || 'General'}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ mr: 1, mb: 1 }}
          />
          <Chip
            label={module.difficulty || 'Beginner'}
            size="small"
            color="secondary"
            variant="outlined"
            sx={{ mr: 1, mb: 1 }}
          />
          {module.isPublic && (
            <Chip
              label="Public"
              size="small"
              color="success"
              variant="outlined"
              sx={{ mr: 1, mb: 1 }}
            />
          )}
        </Box>
        
        {module.tags && module.tags.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {module.tags.slice(0, 3).map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                variant="filled"
                sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }}
              />
            ))}
            {module.tags.length > 3 && (
              <Chip
                label={`+${module.tags.length - 3}`}
                size="small"
                variant="filled"
                sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        )}
        
        {module.files && module.files.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {module.files.slice(0, 2).map((file, index) => (
              <Chip
                key={index}
                label={file.title || `Document ${index + 1}`}
                size="small"
                variant="outlined"
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
            {module.files.length > 2 && (
              <Chip
                label={`+${module.files.length - 2} more`}
                size="small"
                variant="outlined"
                sx={{ mr: 1, mb: 1 }}
              />
            )}
          </Box>
        )}
        
        <Typography variant="caption" color="text.secondary">
          Created: {new Date(module.createdAt).toLocaleDateString()}
        </Typography>
      </CardContent>

      <CardActions>
        <Button
          size="small"
          startIcon={<Icon icon={eyeFill} />}
          onClick={() => onView(module)}
        >
          View
        </Button>
        <Button
          size="small"
          startIcon={<Icon icon={editFill} />}
          onClick={() => onEdit(module)}
        >
          Edit
        </Button>
        <Button
          size="small"
          color="error"
          startIcon={<Icon icon={trash2Fill} />}
          onClick={() => onDelete(module.id)}
        >
          Delete
        </Button>
      </CardActions>
    </Card>
  );
}

// Create/Edit Dialog Component
function CreateEditDialog({ open, onClose, onSave, editData, type }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    urls: [],
    files: [],
    category: 'general',
    difficulty: 'beginner',
    tags: [],
    isPublic: false,
    requiredRoles: [],
    completionTracking: { enabled: false, requiredItems: 0 }
  });
  const [currentUrl, setCurrentUrl] = useState({ title: '', url: '' });
  const [currentFile, setCurrentFile] = useState({ title: '', url: '', fileType: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editData) {
      setFormData({
        title: editData.title || '',
        description: editData.description || '',
        urls: editData.urls || [],
        files: editData.files || [],
        category: editData.category || 'general',
        difficulty: editData.difficulty || 'beginner',
        tags: editData.tags || [],
        isPublic: editData.isPublic || false,
        requiredRoles: editData.requiredRoles || [],
        completionTracking: editData.completionTracking || { enabled: false, requiredItems: 0 }
      });
    } else {
      setFormData({
        title: '',
        description: '',
        urls: [],
        files: [],
        category: 'general',
        difficulty: 'beginner',
        tags: [],
        isPublic: false,
        requiredRoles: [],
        completionTracking: { enabled: false, requiredItems: 0 }
      });
    }
  }, [editData, open]);

  const handleAddUrl = () => {
    if (currentUrl.title && currentUrl.url) {
      setFormData(prev => ({
        ...prev,
        urls: [...prev.urls, { ...currentUrl, id: Date.now() }]
      }));
      setCurrentUrl({ title: '', url: '' });
    }
  };

  const handleAddFile = () => {
    if (currentFile.title && currentFile.url) {
      setFormData(prev => ({
        ...prev,
        files: [...prev.files, { ...currentFile, id: Date.now() }]
      }));
      setCurrentFile({ title: '', url: '', fileType: '' });
    }
  };

  const handleRemoveItem = (index, itemType) => {
    setFormData(prev => ({
      ...prev,
      [itemType]: prev[itemType].filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    if (!formData.title.trim() || saving) return;
    
    setSaving(true);
    try {
      const dataToSave = {
        ...formData,
        type: type,
        id: editData?.id
      };
      
      const result = await onSave(dataToSave);
      if (result && result.success) {
        onClose();
      }
      // If result.success is false, the dialog remains open
    } finally {
      setSaving(false);
    }
  };

  const isVideo = type === 'video';
  const currentItems = isVideo ? formData.urls : formData.files;
  const currentItem = isVideo ? currentUrl : currentFile;
  const setCurrentItem = isVideo ? setCurrentUrl : setCurrentFile;

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {editData ? 'Edit' : 'Create'} Training {isVideo ? 'Video' : 'Module'}
          {saving && <CircularProgress size={20} />}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            variant="outlined"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            sx={{ mb: 3 }}
          />

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                >
                  <MenuItem value="general">General</MenuItem>
                  <MenuItem value="onboarding">Onboarding</MenuItem>
                  <MenuItem value="compliance">Compliance</MenuItem>
                  <MenuItem value="skills">Skills Development</MenuItem>
                  <MenuItem value="safety">Safety</MenuItem>
                  <MenuItem value="policies">Policies</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Difficulty</InputLabel>
                <Select
                  value={formData.difficulty}
                  onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e.target.value }))}
                >
                  <MenuItem value="beginner">Beginner</MenuItem>
                  <MenuItem value="intermediate">Intermediate</MenuItem>
                  <MenuItem value="advanced">Advanced</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={8}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={formData.tags}
                onChange={(event, newValue) => {
                  setFormData(prev => ({ ...prev, tags: newValue.filter(Boolean) }));
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option + index} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Tags"
                    placeholder="Add tag and press Enter"
                    helperText="Press Enter to add tags"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <input
                    type="checkbox"
                    id="isPublic"
                    checked={formData.isPublic}
                    onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
                    style={{ marginRight: 8 }}
                  />
                  <label htmlFor="isPublic">Make Public</label>
                </Box>
              </FormControl>
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom>
            Add {isVideo ? 'Video Links' : 'Document Links'}
          </Typography>
          
          <Box sx={{ mb: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={`${isVideo ? 'Video' : 'Document'} Title`}
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={currentItem.title}
                  onChange={(e) => setCurrentItem(prev => ({ ...prev, title: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={`${isVideo ? 'YouTube/Google Drive' : 'PDF/Google Drive'} URL`}
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={currentItem.url}
                  onChange={(e) => setCurrentItem(prev => ({ ...prev, url: e.target.value }))}
                />
              </Grid>
              {!isVideo && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>File Type</InputLabel>
                    <Select
                      value={currentItem.fileType || ''}
                      onChange={(e) => setCurrentItem(prev => ({ ...prev, fileType: e.target.value }))}
                    >
                      <MenuItem value="pdf">PDF</MenuItem>
                      <MenuItem value="doc">Document</MenuItem>
                      <MenuItem value="image">Image</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={isVideo ? 12 : 6}>
                <Button
                  variant="outlined"
                  startIcon={<Icon icon={plusFill} />}
                  onClick={isVideo ? handleAddUrl : handleAddFile}
                  disabled={!currentItem.title || !currentItem.url}
                >
                  Add {isVideo ? 'Video' : 'Document'}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {currentItems.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Added {isVideo ? 'Videos' : 'Documents'} ({currentItems.length})
              </Typography>
              <List dense>
                {currentItems.map((item, index) => (
                  <ListItem key={index} divider>
                    <ListItemText
                      primary={item.title}
                      secondary={item.url}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveItem(index, isVideo ? 'urls' : 'files')}
                        size="small"
                      >
                        <Icon icon={trash2Fill} />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={!formData.title.trim() || saving}
        >
          {saving ? 'Saving...' : (editData ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Helper function to convert YouTube URL to embed URL
function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  
  if (match && match[2].length === 11) {
    return `https://www.youtube.com/embed/${match[2]}`;
  }
  return null;
}

// Helper function to convert Google Drive URL to embed URL
function getGoogleDriveEmbedUrl(url) {
  if (!url) return null;

  // Handle various Google Drive URL patterns
  let fileId = null;

  // Pattern 1: https://drive.google.com/file/d/{FILE_ID}/view?usp=sharing
  if (url.includes('drive.google.com/file/d/')) {
    const match = url.match(/\/file\/d\/([a-zA-Z0-9-_]+)/);
    if (match) {
      fileId = match[1];
    }
  }

  // Pattern 2: https://drive.google.com/open?id={FILE_ID}
  if (url.includes('drive.google.com/open?id=')) {
    const match = url.match(/id=([a-zA-Z0-9-_]+)/);
    if (match) {
      fileId = match[1];
    }
  }

  // Pattern 3: https://docs.google.com/document/d/{FILE_ID}/edit?usp=sharing
  if (url.includes('docs.google.com') && url.includes('/d/')) {
    const match = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
    if (match) {
      fileId = match[1];
    }
  }

  // Pattern 4: Direct file ID in URL
  if (!fileId && url.includes('drive.google.com')) {
    const match = url.match(/([a-zA-Z0-9-_]{33,})/);
    if (match && match[1].length >= 33) {
      fileId = match[1];
    }
  }

  if (fileId) {
    return `https://drive.google.com/file/d/${fileId}/preview`;
  }

  return null;
}

// Helper function to check if URL is a direct PDF
function isPdfUrl(url) {
  return url && (url.toLowerCase().endsWith('.pdf') || url.includes('.pdf'));
}

// View Dialog Component
function ViewDialog({ open, onClose, data, type }) {
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (open) {
      setCurrentItemIndex(0);
      setLoading(true);
    }
  }, [open]);

  if (!data) return null;

  const isVideo = type === 'video';
  const items = isVideo ? data.urls : data.files;
  const currentItem = items?.[currentItemIndex];

  const handleOpenExternalLink = (url) => {
    window.open(url, '_blank');
  };

  const renderMediaViewer = () => {
    if (!currentItem) return null;

    const { url } = currentItem;

    if (isVideo) {
      // Handle YouTube videos
      const youtubeEmbedUrl = getYouTubeEmbedUrl(url);
      if (youtubeEmbedUrl) {
        return (
          <Box sx={{ position: 'relative', width: '100%', height: '500px', mb: 2 }}>
            <iframe
              src={youtubeEmbedUrl}
              title={currentItem.title}
              width="100%"
              height="100%"
              frameBorder="0"
              allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              onLoad={() => setLoading(false)}
              style={{ borderRadius: '8px' }}
            />
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  borderRadius: '8px'
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </Box>
        );
      }

      // Handle Google Drive videos
      const googleDriveEmbedUrl = getGoogleDriveEmbedUrl(url);
      if (googleDriveEmbedUrl) {
        return (
          <Box sx={{ position: 'relative', width: '100%', height: '500px', mb: 2 }}>
            <iframe
              src={googleDriveEmbedUrl}
              title={currentItem.title}
              width="100%"
              height="100%"
              frameBorder="0"
              onLoad={() => setLoading(false)}
              style={{ borderRadius: '8px' }}
            />
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  borderRadius: '8px'
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </Box>
        );
      }

      // Handle other video URLs (direct video files)
      return (
        <Box sx={{ width: '100%', mb: 2 }}>
          <video
            controls
            width="100%"
            height="500"
            style={{ borderRadius: '8px' }}
            onLoadStart={() => setLoading(true)}
            onCanPlay={() => setLoading(false)}
          >
            <source src={url} type="video/mp4" />
            <source src={url} type="video/webm" />
            <source src={url} type="video/ogg" />
            Your browser does not support the video tag.
          </video>
        </Box>
      );
    } else {
      // Handle documents
      const googleDriveEmbedUrl = getGoogleDriveEmbedUrl(url);
      
      if (googleDriveEmbedUrl) {
        return (
          <Box sx={{ position: 'relative', width: '100%', height: '600px', mb: 2 }}>
            <iframe
              src={googleDriveEmbedUrl}
              title={currentItem.title}
              width="100%"
              height="100%"
              frameBorder="0"
              onLoad={() => setLoading(false)}
              style={{ borderRadius: '8px' }}
            />
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  borderRadius: '8px'
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </Box>
        );
      }
      
      // Handle direct PDF URLs
      if (isPdfUrl(url)) {
        return (
          <Box sx={{ width: '100%', height: '600px', mb: 2 }}>
            <iframe
              src={url}
              title={currentItem.title}
              width="100%"
              height="100%"
              frameBorder="0"
              style={{ borderRadius: '8px' }}
            />
          </Box>
        );
      }
      
      // Fallback for unsupported formats
      return (
        <Paper sx={{ p: 3, textAlign: 'center', mb: 2 }}>
          <Icon icon={fileFill} width={48} height={48} color="text.secondary" />
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Preview not available
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            This document type cannot be previewed directly.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Icon icon={linkFill} />}
            onClick={() => handleOpenExternalLink(url)}
          >
            Open in New Tab
          </Button>
        </Paper>
      );
    }
  };

  const handlePrevious = () => {
    if (currentItemIndex > 0) {
      setCurrentItemIndex(currentItemIndex - 1);
      setLoading(true);
    }
  };

  const handleNext = () => {
    if (currentItemIndex < items.length - 1) {
      setCurrentItemIndex(currentItemIndex + 1);
      setLoading(true);
    }
  };

  return (
    <Portal>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="lg" 
        fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: { 
          minHeight: fullScreen ? '100vh' : '80vh',
          maxHeight: '95vh'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h5">{data.title}</Typography>
            {items && items.length > 0 && (
              <Typography variant="subtitle2" color="text.secondary">
                {isVideo ? 'Video' : 'Document'} {currentItemIndex + 1} of {items.length}: {currentItem?.title}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <Icon icon={closeFill} />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 1 }}>
        {/* Media Viewer */}
        {renderMediaViewer()}
        
        {/* Navigation Controls */}
        {items && items.length > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 3 }}>
            <Button
              variant="outlined"
              onClick={handlePrevious}
              disabled={currentItemIndex === 0}
              startIcon={<Icon icon="eva:arrow-left-fill" />}
            >
              Previous
            </Button>
            
            <Typography variant="body2" sx={{ mx: 2 }}>
              {currentItemIndex + 1} / {items.length}
            </Typography>
            
            <Button
              variant="outlined"
              onClick={handleNext}
              disabled={currentItemIndex === items.length - 1}
              endIcon={<Icon icon="eva:arrow-right-fill" />}
            >
              Next
            </Button>
          </Box>
        )}

        {/* Description */}
        <Typography variant="body1" paragraph>
          {data.description}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* Additional Actions */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Icon icon={linkFill} />}
            onClick={() => handleOpenExternalLink(currentItem?.url)}
            disabled={!currentItem}
          >
            Open in New Tab
          </Button>
          <Button
            variant="outlined"
            startIcon={<Icon icon={shareFill} />}
            onClick={() => {
              if (currentItem?.url) {
                navigator.clipboard.writeText(currentItem.url);
                // You might want to add a snackbar notification here
              }
            }}
            disabled={!currentItem}
          >
            Copy Link
          </Button>
        </Box>

        {/* Item List */}
        {items && items.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              All {isVideo ? 'Videos' : 'Documents'} ({items.length})
            </Typography>
            <List dense>
              {items.map((item, index) => (
                <ListItem 
                  key={index} 
                  divider
                  button
                  selected={index === currentItemIndex}
                  onClick={() => {
                    setCurrentItemIndex(index);
                    setLoading(true);
                  }}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    backgroundColor: index === currentItemIndex ? 'action.selected' : 'transparent'
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Icon 
                          icon={isVideo ? playCircleFill : fileFill} 
                          style={{ marginRight: 8 }}
                        />
                        {item.title}
                        {index === currentItemIndex && (
                          <Chip 
                            label="Current" 
                            size="small" 
                            color="primary" 
                            sx={{ ml: 1 }} 
                          />
                        )}
                      </Box>
                    }
                    secondary={item.url}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenExternalLink(item.url);
                      }}
                    >
                      <Icon icon={linkFill} />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      </Dialog>
    </Portal>
  );
}

export default function AdminTrainingAndDevelopment() {
  const { enqueueSnackbar } = useSnackbar();
  const [currentTab, setCurrentTab] = useState(0);
  const [trainingVideos, setTrainingVideos] = useState([]);
  const [trainingModules, setTrainingModules] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [dialogType, setDialogType] = useState('video'); // 'video' or 'module'
  const [userData, setUserData] = useState(null);
  
  // Company selection for group accounts
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');

  useEffect(() => {
    const getUserData = async () => {
      const user = await storage.getUser();
      const parsedUser = JSON.parse(user);
      setUserData(parsedUser);
      
      // Load companies for group accounts (role >= 3)
      if (parsedUser && parsedUser.role >= 3) {
        try {
          const storesRes = await usersAPI.get_group_store(parsedUser._id);
          if (storesRes && storesRes.status === 200 && storesRes.data?.report?.store) {
            const stores = storesRes.data.report.store;
            const sortedStores = stores
              .slice()
              .sort((a, b) => a.trim().replace(/\s+/g, ' ').localeCompare(b.trim().replace(/\s+/g, ' ')));
            setCompanies([...sortedStores, parsedUser.company]);
            setSelectedCompany(parsedUser.company); // Default to user's own company
          }
        } catch (error) {
          console.error('Failed to fetch companies', error);
          enqueueSnackbar('Failed to fetch company list', { variant: 'error' });
        }
      } else {
        setCompanies([]);
        setSelectedCompany(parsedUser.company);
      }
    }
    if(!userData) getUserData();
  }, [enqueueSnackbar]);

  const loadTrainingMaterials = useCallback(async () => {
    if (!userData || !selectedCompany) return;
    try {
      setLoading(true);
      const response = await usersAPI.get_training_materials({ company: selectedCompany });
      if (response.ok) {
        if(response.data.data.length > 0) {
          const videos = response.data.data.filter(item => item.type === 'video');
          const modules = response.data.data.filter(item => item.type === 'module');
          setTrainingVideos(videos);
          setTrainingModules(modules);
        } else {
          setTrainingVideos([]);
          setTrainingModules([]);
        }
      }
    } catch (error) {
      console.error('Error loading training materials:', error);
      enqueueSnackbar('Error loading training materials', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [userData, selectedCompany, enqueueSnackbar]);

  useEffect(() => {
    if(userData && selectedCompany) loadTrainingMaterials();
  }, [userData, selectedCompany, loadTrainingMaterials]);

  const handleCreateNew = (type) => {
    setDialogType(type);
    setEditData(null);
    setCreateDialogOpen(true);
  };

  const handleEdit = (item) => {
    setDialogType(item.type);
    setEditData(item);
    setCreateDialogOpen(true);
  };

  const handleView = (item) => {
    setDialogType(item.type);
    setViewData(item);
    setViewDialogOpen(true);
  };

  const handleSave = async (data) => {
    try {
      const payload = {
        ...data,
        company: selectedCompany
      };

      let response;
      if (data.id) {
        response = await usersAPI.update_training_material(data.id, payload);
      } else {
        response = await usersAPI.create_training_material(payload);
      }

      if (response.data) {
        const actionType = data.id ? 'updated' : 'created';
        enqueueSnackbar(
          `Training ${data.type} ${actionType} successfully!`,
          { variant: 'success' }
        );
        
        // Log successful action
        logSuccessAction({
          user: userData._id,
          company: selectedCompany,
          userDisplay: {
            name: userData.displayName,
            email: userData.email,
          },
          role: userData.role,
          action: `${actionType.charAt(0).toUpperCase() + actionType.slice(1)} training ${data.type}`,
          category: ACTION_CATEGORIES.COMPANY_CONTENT,
          details: `Training ${data.type} "${data.title}" was ${actionType} successfully.`
        });
        
        loadTrainingMaterials();
        return { success: true };
      } else {
        enqueueSnackbar('Error saving training material', { variant: 'error' });
        return { success: false, error: 'No data returned from server' };
      }
    } catch (error) {
      console.error('Error saving training material:', error);
      enqueueSnackbar('Error saving training material', { variant: 'error' });
      return { success: false, error: error.message };
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this training material?')) {
      try {
        // Find the item being deleted for logging purposes
        const allItems = [...trainingVideos, ...trainingModules];
        const itemToDelete = allItems.find(item => item.id === id);
        
        await usersAPI.delete_training_material(id);
        enqueueSnackbar('Training material deleted successfully!', { variant: 'success' });
        
        // Log successful action
        if (itemToDelete) {
          logSuccessAction({
            user: userData._id,
            company: selectedCompany,
            userDisplay: {
              name: userData.displayName,
              email: userData.email,
            },
            role: userData.role,
            action: `Deleted training ${itemToDelete.type}`,
            category: ACTION_CATEGORIES.COMPANY_CONTENT,
            details: `Training ${itemToDelete.type} "${itemToDelete.title}" was deleted successfully.`
          });
        }
        
        loadTrainingMaterials();
      } catch (error) {
        console.error('Error deleting training material:', error);
        enqueueSnackbar('Error deleting training material', { variant: 'error' });
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Grid container spacing={3}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card>
                <Skeleton variant="rectangular" height={200} />
                <CardContent>
                  <Skeleton variant="text" height={32} />
                  <Skeleton variant="text" height={20} width="80%" />
                  <Skeleton variant="text" height={20} width="60%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      );
    }

    return (
      <>
        <TabPanel value={currentTab} index={0}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5">Training Videos</Typography>
            <Button
              variant="contained"
              startIcon={<Icon icon={plusFill} />}
              onClick={() => handleCreateNew('video')}
            >
              Add Training Video
            </Button>
          </Box>

          {trainingVideos.length > 0 ? (
            <Grid container spacing={3}>
              {trainingVideos.map((video) => (
                <Grid item xs={12} sm={6} md={4} key={video.id}>
                  <TrainingVideoCard
                    video={video}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onView={handleView}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Icon icon={videoFill} width={64} height={64} color="text.secondary" />
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                No training videos yet
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Create your first training video to get started
              </Typography>
              <Button
                variant="contained"
                startIcon={<Icon icon={plusFill} />}
                onClick={() => handleCreateNew('video')}
              >
                Add Training Video
              </Button>
            </Paper>
          )}
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5">Training Modules</Typography>
            <Button
              variant="contained"
              startIcon={<Icon icon={plusFill} />}
              onClick={() => handleCreateNew('module')}
            >
              Add Training Module
            </Button>
          </Box>

          {trainingModules.length > 0 ? (
            <Grid container spacing={3}>
              {trainingModules.map((module) => (
                <Grid item xs={12} sm={6} md={4} key={module.id}>
                  <TrainingModuleCard
                    module={module}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onView={handleView}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Icon icon={fileFill} width={64} height={64} color="text.secondary" />
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                No training modules yet
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Create your first training module to get started
              </Typography>
              <Button
                variant="contained"
                startIcon={<Icon icon={plusFill} />}
                onClick={() => handleCreateNew('module')}
              >
                Add Training Module
              </Button>
            </Paper>
          )}
        </TabPanel>
      </>
    );
  };

  return (
    <Page title="Training & Development">
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 4 }}>
          Training & Development
        </Typography>

        {/* Company Selector for Group Accounts */}
        {userData && userData.role >= 3 && companies.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Autocomplete
                  value={selectedCompany || ''}
                  onChange={(event, newValue) => {
                    setSelectedCompany(newValue || '');
                  }}
                  options={companies}
                  getOptionLabel={(option) => option}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Company"
                      placeholder="Choose company to manage..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon icon={personFill} />
                          </InputAdornment>
                        )
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Typography variant="body2">
                        {option}
                      </Typography>
                    </Box>
                  )}
                  isOptionEqualToValue={(option, value) => option === value}
                  clearOnBlur
                  selectOnFocus
                  handleHomeEndKeys
                />
              </Grid>
            </Grid>
          </Box>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab 
              label="Training Videos" 
              icon={<Icon icon={videoFill} />}
              iconPosition="start"
            />
            <Tab 
              label="Training Modules" 
              icon={<Icon icon={fileFill} />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {renderContent()}

        {/* Create/Edit Dialog */}
        <CreateEditDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSave={handleSave}
          editData={editData}
          type={dialogType}
        />

        {/* View Dialog */}
        <ViewDialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          data={viewData}
          type={dialogType}
        />
      </Container>
    </Page>
  );
}
